/**
 * Created by rathompson on 9/15/21
 * File: RemoveIdentityModal
 * Project: Cloud Management Module ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Body,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader, LoadingIcon, Bold,
} from '@panwds/react-ui'
import {
  BUTTON_REMOVE,
  BUTTON_CANCEL,
  LABEL_REMOVE_IDENTITY,
  LABEL_REMOVE_MANY,
  LABEL_REMOVE_SINGLE,
  ERROR_SERVER_REMOVE
} from '../../../constants/LabelConstants'
import StringTemplate from '../../../../common/utils/StringTemplate'
import {
  DATA_TEST_REMOVE_IDENTITY_MESSAGE,
  DATA_TEST_REMOVE_IDENTITY_SUBMIT,
  DATA_TEST_REMOVE_IDENTITY_CANCEL
} from '../../../../common/constants/TestingDataIds'

import '../../../styles/Modals.scss'
import { SERVER_ERROR_FAIL_TO_REMOVE_LAST_SUPERUSER } from '../../../constants/IAMConstants'

/***
 *
 * @param closeModal callback when cancel/close button is clicked
 * @param isOpen boolean indicating whether modal is visible or not
 * @param tenant currently selected tenant
 * @param getData callback to refresh identity table
 * @param removeIdentity callback for delete identity service
 * @param identities array of selected identities
 * @returns {JSX.Element}
 * @constructor
 * @desc Modal display and remove all selected identities
 */
const RemoveIdentityModal = ({
  closeModal: _closeModal,
  isOpen,
  tenant,
  getData,
  removeIdentity,
  identities
}) => {

  /**
   *     ========= Local State ===========
   */
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [response, setResponse] = useState(null)

  /**
   *     ========= Callback =============
   */
  const closeModal = useCallback(() => {
    _closeModal()
    if (error) {
      getData()
    }
  }, [_closeModal, error, getData])

  const onRemove = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const res = await removeIdentity(tenant?.id, identities)
      setResponse(res)
    }
    catch (error) {
      if (error.message === SERVER_ERROR_FAIL_TO_REMOVE_LAST_SUPERUSER) {
        _closeModal()
        getData()
      }
      else {
        setError(error.message || ERROR_SERVER_REMOVE)
      }
    }
    setIsLoading(false)
  }, [removeIdentity, tenant?.id, identities, _closeModal, getData])

  const renderIdentities = useCallback(() => {
    return identities?.map((identity, index) => (
      <li key={index}><Body>{identity.identity_email}</Body></li>
    ))
  }, [identities])

  /**
   *     ========= Lifecycle =============
   */
  useEffect(() => {
    if (response) {
      if (response.status === 200 || response.status === 201 || response.status === 204) {
        closeModal()
        getData()
      }
      else {
        setError(ERROR_SERVER_REMOVE)
      }
    }
  }, [closeModal, getData, response])

  const isMany = identities?.length > 1
  const message = isMany ?
    StringTemplate(LABEL_REMOVE_MANY, [identities?.length]) :
    StringTemplate(LABEL_REMOVE_SINGLE, [(identities || [])[0]?.identity_email])

  return (
    <Modal style={{ top: '30%', maxHeight: 'calc(48vh - 5.625rem)', minHeight: 'calc(24vh - 5.625rem)' }} onClose={closeModal} isOpen={isOpen}>
      <ModalHeader title={LABEL_REMOVE_IDENTITY}/>
      {isLoading ? (
        <ModalBody addClassName='tw-flex tw-flex-row tw-justify-center tw-items-center'>
          <LoadingIcon size="5xl"/>
        </ModalBody>
      ) : (
        <ModalBody addClassName='tw-overflow-y-scroll'>
          {error && <Bold appearance='semibold' addClassName='tw-text-error'>{error}</Bold>}
          <Body data-testid={DATA_TEST_REMOVE_IDENTITY_MESSAGE} addClassName='tw-break-words'>{message} from <Bold>{tenant?.display_name}</Bold></Body>
          {isMany && (
            <ul>
              {renderIdentities()}
            </ul>
          )}
        </ModalBody>
      )}
      <ModalFooter>
        <Button
          data-testid={DATA_TEST_REMOVE_IDENTITY_CANCEL}
          onClick={closeModal}
          appearance="secondary"
          disabled={isLoading}
        >{BUTTON_CANCEL}</Button>
        <ModalCloseButton
          data-testid={DATA_TEST_REMOVE_IDENTITY_SUBMIT}
          onClick={onRemove.bind(this)}
          appearance='secondary-destructive'
          disabled={isLoading || error}
        >{BUTTON_REMOVE}</ModalCloseButton>
      </ModalFooter>
    </Modal>
  )
}

export default RemoveIdentityModal
